// Web Push client helper (v226.9.7)
// Subscribes/unsubscribes this browser to push and registers the subscription
// with the API. The per-category "Push" pill in NotificationPreferencesScreen
// gates WHICH events push; this module handles the device-level subscription
// (one PushSubscription per browser) and the OS permission prompt.

import { pushAPI } from './api'

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

// VAPID public key is base64url; PushManager needs a Uint8Array.
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = (typeof window !== 'undefined' && window.atob ? window.atob : globalThis.atob)(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

// ═══ PROD-637 (patient P2) · A SUBSCRIPTION MADE WITH ANOTHER KEY IS RENEWED ═══════════════
// The same rule as the MD panel (md10a src/services/push.js, check-push-key).
// ★★★ WHY. The VAPID key pair is being replaced (the served public key had no usable private half:
//   VAPID_PRIVATE_KEY was a placeholder in production, so no push was ever sent). Every browser
//   subscription made with the old public key is bound to it - the push service refuses a message
//   signed by another key - so after the swap those browsers would hold a subscription that looks
//   live and receives nothing. ensurePushSubscribed therefore compares the existing subscription's
//   applicationServerKey with the key the API serves now and re-subscribes on a mismatch (the old
//   endpoint is unsubscribed in the browser and removed from the API, best-effort).
// ★★ UNKNOWN IS NOT A MISMATCH. A browser that does not expose options.applicationServerKey, or a
//   served key that cannot be read, keeps its subscription exactly as before (null below): never
//   unsubscribe on unknown.

/** true / false, or null when either side cannot be read. */
export function sameApplicationServerKey(existingKey, publicKeyB64) {
  if (!existingKey || !publicKeyB64) return null
  let a, b
  try {
    a = new Uint8Array(existingKey instanceof ArrayBuffer ? existingKey : (existingKey.buffer ? existingKey.buffer.slice(existingKey.byteOffset, existingKey.byteOffset + existingKey.byteLength) : existingKey))
    b = urlBase64ToUint8Array(publicKeyB64)
  } catch (e) { return null }
  if (!a.length || !b.length) return null
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

async function servedPublicKey() {
  try {
    const res = await pushAPI.getVapidKey()
    return { key: (res && res.publicKey) || null, failed: false }
  } catch (e) {
    return { key: null, failed: true }
  }
}

/** Drop a subscription bound to a key the API no longer serves; returns null so the caller subscribes again. */
async function dropStale(subscription) {
  const endpoint = subscription.endpoint
  try { await subscription.unsubscribe() } catch (e) { /* the browser keeps it; the subscribe below replaces it */ }
  try { await pushAPI.unsubscribe(endpoint) } catch (e) { /* best-effort: the API prunes a dead endpoint on its own */ }
  return null
}

/**
 * PROD-637: renew a stale subscription without any prompt - only when permission is already granted
 * and a subscription exists. App runs it once per signed-in session, so browsers subscribed with the
 * old key move to the new one without a visit to Settings. Returns { renewed: boolean, reason? }.
 */
export async function renewPushIfKeyChanged() {
  if (!isPushSupported() || Notification.permission !== 'granted') return { renewed: false, reason: 'not_granted' }
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return { renewed: false, reason: 'no_subscription' }
    const { key } = await servedPublicKey()
    if (sameApplicationServerKey(subscription.options && subscription.options.applicationServerKey, key) !== false) {
      // ★★★ PROD-1181 / PROD-637 (patient part): EQUAL OR UNKNOWN KEY STILL TELLS THE SERVER.
      //   The browser can hold a live subscription the server has no row for (a failed first POST that
      //   was never retried, a row pruned or re-assigned, an active logout that removed it). Before this
      //   the skip sent nothing, so that device stayed silent for ever. The subscription is POSTed again
      //   once per signed-in session: the API upserts by endpoint, so it is idempotent. No prompt, nothing
      //   is dropped or made, and a refusal (server down) is swallowed here and tried again next session.
      try {
        await pushAPI.subscribe(subscription.toJSON(), navigator.userAgent)
        return { renewed: false, reason: 'current_or_unknown', registered: true }
      } catch (e) {
        return { renewed: false, reason: 'current_or_unknown', registered: false }
      }
    }
    await dropStale(subscription)
    const fresh = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) })
    await pushAPI.subscribe(fresh.toJSON(), navigator.userAgent)
    return { renewed: true }
  } catch (e) {
    return { renewed: false, reason: 'failed' }
  }
}

/**
 * Ensure this browser is subscribed to push and registered with the API.
 * Returns { ok: boolean, reason?: string }.
 * Safe to call repeatedly — it reuses an existing subscription when present.
 */
export async function ensurePushSubscribed() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }

  // Ask the OS/browser for permission. Must be triggered by a user gesture.
  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }
  if (permission !== 'granted') return { ok: false, reason: 'denied' }

  const registration = await navigator.serviceWorker.ready

  // Reuse an existing subscription if there is one - unless it was made with another key.
  let subscription = await registration.pushManager.getSubscription()
  // ★ PROD-637: the served key is read every time, so an existing subscription can be compared with it.
  const served = await servedPublicKey()
  if (subscription && sameApplicationServerKey(subscription.options && subscription.options.applicationServerKey, served.key) === false) {
    subscription = await dropStale(subscription)
  }
  if (!subscription) {
    if (served.failed) return { ok: false, reason: 'no_vapid_key' }
    const publicKey = served.key
    if (!publicKey) return { ok: false, reason: 'push_not_configured' }

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    })
  }

  try {
    await pushAPI.subscribe(subscription.toJSON(), navigator.userAgent)
  } catch (e) {
    return { ok: false, reason: 'register_failed' }
  }
  return { ok: true }
}

/**
 * Unsubscribe this browser from push and remove it from the API.
 * Other devices and the in-app feed are unaffected.
 */
export async function unsubscribePush() {
  if (!isPushSupported()) return { ok: true }
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      const endpoint = subscription.endpoint
      await subscription.unsubscribe()
      try { await pushAPI.unsubscribe(endpoint) } catch (e) { /* best-effort */ }
    }
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: 'unsubscribe_failed' }
  }
}

// ═══ PROD-1181 · AN ACTIVE LOG OUT REMOVES THIS BROWSER FROM THE SERVER, NOT FROM THE BROWSER ═══
// The patient taps Log out (menu, or "Log Out Now" in the idle warning): this browser's endpoint is removed
// at the API while the token still exists, so the next person on a shared device receives no pushes meant
// for this patient. The browser subscription is KEPT: the next sign-in re-registers it
// (renewPushIfKeyChanged above). An automatic end of session (inactivity timeout, expired session,
// PIN failures, privacy lock) never calls this: the patient did not choose to leave.
// Never prompts, never throws, and gives up after `maxWaitMs` so a slow server cannot hold the log out.
export async function unsubscribePushOnServer(maxWaitMs = 1500) {
  if (!isPushSupported() || Notification.permission !== 'granted') return { ok: true, reason: 'nothing_to_do' }
  const work = (async () => {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (!subscription) return { ok: true, reason: 'no_subscription' }
      await pushAPI.unsubscribe(subscription.endpoint)
      return { ok: true }
    } catch (e) {
      return { ok: false, reason: 'failed' }
    }
  })()
  let timer
  const limit = new Promise((resolve) => { timer = setTimeout(() => resolve({ ok: false, reason: 'timeout' }), maxWaitMs) })
  try { return await Promise.race([work, limit]) } finally { clearTimeout(timer) }
}
